<template>
  <section class="space-y-4">
    <header class="flex flex-wrap items-center justify-between gap-3">
      <div class="flex items-center gap-2">
        <button
          v-for="pill in statusPills" :key="pill.value"
          class="px-3 py-1 rounded-full text-sm border"
          :class="statusFilter === pill.value
            ? 'bg-primary-500 border-primary-500 text-white'
            : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-primary-400'"
          @click="setStatusFilter(pill.value)">
          {{ pill.label }}
          <span v-if="counts[pill.value] !== undefined" class="opacity-75">({{ counts[pill.value] }})</span>
        </button>
      </div>
      <div class="flex items-center gap-2">
        <select
          v-model="intentFilter"
          class="rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-700 text-sm"
          @change="fetchPage(1)">
          <option value="">All intents</option>
          <option v-for="(meta, value) in INTENTS" :key="value" :value="value">
            {{ meta.label }}
          </option>
        </select>
        <select
          v-model="platformFilter"
          class="rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-700 text-sm"
          @change="fetchPage(1)">
          <option value="">All platforms</option>
          <option value="reddit">Reddit</option>
          <option value="facebook">Facebook</option>
          <option value="nextdoor">Nextdoor</option>
          <option value="other">Other</option>
        </select>
        <select
          v-model="monitorFilter"
          class="rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-700 text-sm"
          @change="fetchPage(1)">
          <option value="">All monitors</option>
          <option v-for="monitor in monitors" :key="monitor.id" :value="monitor.id">
            {{ monitor.name }}
          </option>
        </select>
      </div>
    </header>

    <div v-if="loading" class="py-12 text-center text-gray-400">
      <i class="far fa-spinner fa-spin text-2xl" />
    </div>

    <div
      v-else-if="findings.length === 0"
      class="rounded-xl border border-dashed border-gray-300 dark:border-gray-700 p-8 text-center">
      <i class="far fa-satellite-dish text-3xl text-gray-300 dark:text-gray-600 mb-3" />
      <p class="text-sm font-medium text-gray-700 dark:text-gray-200">
        No conversations found yet
      </p>
      <p class="text-sm text-gray-500 mt-1 max-w-md mx-auto">
        Findings will appear here after a monitor run. Run a monitor now or wait for
        the next scheduled scan.
      </p>
    </div>

    <div v-else class="space-y-3">
      <FindingCard
        v-for="finding in findings" :key="finding.id"
        :finding="finding"
        @set-status="setStatus"
        @view="openDetail" />
    </div>

    <div v-if="totalPages > 1" class="flex items-center justify-center gap-2 pt-2">
      <button class="btn btn-secondary btn-sm" :disabled="page <= 1" @click="fetchPage(page - 1)">
        Previous
      </button>
      <span class="text-sm text-gray-500">Page {{ page }} of {{ totalPages }}</span>
      <button class="btn btn-secondary btn-sm" :disabled="page >= totalPages" @click="fetchPage(page + 1)">
        Next
      </button>
    </div>

    <UiSlidePanel ref="detailPanel" />
  </section>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import FindingCard from './finding_card.vue'
import FindingDetail from './finding_detail.vue'
import { INTENTS } from './intents'
import { UiSlidePanel } from '~/javascript/components/ui/slide_panel'
import { getFindings, updateFinding } from '~/javascript/api/social_monitors'
import { toastError } from '~/javascript/utils/toast'

const props = defineProps({
  businessId: { type: String, required: true },
  monitors: { type: Array, default: () => [] }
})

const emit = defineEmits(['changed'])

const statusPills = [
  { value: 'new', label: 'New' },
  { value: 'reviewed', label: 'Reviewed' },
  { value: 'dismissed', label: 'Dismissed' }
]

const findings = ref([])
const counts = ref({})
const loading = ref(false)
const statusFilter = ref('new')
const platformFilter = ref('')
const intentFilter = ref('')
const monitorFilter = ref('')
const page = ref(1)
const totalPages = ref(1)

const fetchPage = async(targetPage = 1) => {
  loading.value = true
  try {
    const { data, headers } = await getFindings(props.businessId, {
      status: statusFilter.value || undefined,
      platform: platformFilter.value || undefined,
      intent: intentFilter.value || undefined,
      monitor_id: monitorFilter.value || undefined,
      page: targetPage
    })
    findings.value = data.findings
    counts.value = { new: 0, reviewed: 0, dismissed: 0, ...data.counts }
    page.value = targetPage
    totalPages.value = parseInt(headers['total-pages'] || '1', 10)
  } catch (e) {
    toastError(e)
  } finally {
    loading.value = false
  }
}

const setStatusFilter = (value) => {
  statusFilter.value = value
  fetchPage(1)
}

const detailPanel = ref(null)

const setStatus = async(finding, status) => {
  try {
    await updateFinding(finding.id, { status })
    fetchPage(page.value)
    emit('changed')
  } catch (e) {
    toastError(e)
  }
}

const openDetail = (finding) => {
  detailPanel.value.open({
    component: FindingDetail,
    props: { finding },
    events: {
      'set-status': (target, status) => {
        detailPanel.value.close()
        setStatus(target, status)
      }
    }
  })
}

defineExpose({ refresh: () => fetchPage(page.value) })

onMounted(() => fetchPage(1))
</script>
